import { HelperFlowService } from '@app/shared/services/helper-flow.service';
import { Injectable } from '@angular/core';
import { Logger, AuthenticationService, GlobalInfoService } from '@app/core';
import {
  Actividad,
  Const,
  Microproceso,
  Tarea,
  TareaCondicionAdmin,
  Tramite,
  TramiteDefaultAdmin,
  UsuarioActual,
  TareaOpcionAdmin,
  TypeProcess
} from '@app/shared';
import { find, findIndex, isEmpty, orderBy, minBy, min, omit, remove, clone, isNumber } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ProcessAutomationService } from './process-automation.service';

const log = new Logger('StorageService');

type ActionHistoryType = 'create' | 'update' | 'delete';
export interface ActionHistory {
  activityId: number;
  taskId?: number;
}
type GroupActionHistoryType = 'activities' | 'tasks';
export interface GroupActionHistory {
  activities: ActionHistory[];
  tasks: ActionHistory[];
}

export interface FlowOrigin {
  category: number;
  process: number;
  tramite: number;
}

// Estructura de los flujos almacenados en LocalStorage
export interface LocalFlow {
  tramite: Tramite;
  microproces: Microproceso[];
  meta: {
    // Fecha de creación del flujo.
    created: Date;
    // Define si el flujo tiene cambios pendientes por sincronizar, por defecto es true cuando no se ha creado en la DB.
    pendingChanges: boolean;
    // Id del usuario autenticado que creó el flujo.
    user: number,
    // Origen del trámite; categoría y proceso.
    origin: FlowOrigin
  };
}
// Tipos de flujos
export type FlowType = 'tramite' | 'tramiteInGoBack' | 'microproces';
// Tipos de consecutivos
type IdType = 'activity' | 'task' | 'option';

/**
 * Se encarga de controlar los datos persistentes de la automatización
 * de procesos, dichos datos pueden ser almacenados en LocalStorage y
 * sincronizados con la DB.
 *
 * @export
 * @class StorageService
 */
@Injectable()
export class StorageService {
  // Entrada del tipo de proceso
  set typeProcess(t: TypeProcess) {
    this._typeProcess = t;
    // Consultar los flujos almacenados en LocalStorage.
    let flows;
    if (t === 'process') {
      flows = this.lss.retrieve(Const.KEY_LS_DEFAULT_FLOWS.process);
    } else {
      flows = this.lss.retrieve(Const.KEY_LS_DEFAULT_FLOWS.secondaryProcess);
    }
    if (flows) {
      this.localFlows = flows;
      if (this.saveFlows) {
        // Eliminar todos los flujos que no estan bloqueados
        // según los criterios de la función 'flowIsBlocked'.
        this.localFlows = this.localFlows.filter((f) => {
          return this.flowIsBlocked(f.tramite.id) ? true : false;
        });
        this.saveInLocal();
      }
    } else {
      if (this.saveFlows) {
        // Si no hay flujos se inicia vacío.
        if (t === 'process') {
          this.lss.store(Const.KEY_LS_DEFAULT_FLOWS.process, []);
        } else {
          this.lss.store(Const.KEY_LS_DEFAULT_FLOWS.secondaryProcess, []);
        }
      }
    }
  }
  // Define si debe o no guardar los flujos inicialmente consultado
  saveFlows = true;
  // Tipo de flujo con el que se esta trabajando actualmente
  currentFlowType: FlowType;
  // Contiene el tipo de proceso que se esta renderizando
  private _typeProcess: TypeProcess;
  // Determina si el flujo esta sincronizado con la DB o no
  private _isSynchronized = false;
  // Define si hay cambios pendientes por guardar
  private _pendingChanges = false;
  // Trámite default o microproceso con el cual se esta trabajando
  private _flow: Tramite | Microproceso;
  // Notifica si la disponibilidad de las actividades del flujo
  private activitiesSub = new BehaviorSubject<Actividad[]>([]);
  // Usuario autenticado
  private authUser: UsuarioActual;
  // Espejo de flujos almacenados en LocalStorage
  private localFlows: LocalFlow[] = [];
  // Historial de acciones en las actividades y tareas
  private creationsHistory: GroupActionHistory = {
    activities: [],
    tasks: []
  };
  private updatesHistory: GroupActionHistory = {
    activities: [],
    tasks: []
  };
  private deletionsHistory: GroupActionHistory = {
    activities: [],
    tasks: []
  };
  // Contador global para generar los IDs,
  // para la creación de actividades y tareas.
  private id = {
    activity: 0,
    task: 0,
    option: 0
  };

  constructor(
    private lss: LocalStorageService,
    private hfs: HelperFlowService,
    private pas: ProcessAutomationService,
    private as: AuthenticationService,
    private gis: GlobalInfoService
  ) {
    // Obtener usuario autenticado
    this.authUser = this.as.getCredentials();
    // Definir por defecto que no hay cambios sin guardar.
    this.pendingChanges = false;
    // Definir por defecto que el flujo no esta sincronizado con la DB.
    this.isSynchronized = false;
  }

  get typeProcess(): TypeProcess {
    return this._typeProcess;
  }

  /**
   * Retorna el flujo actual.
   *
   * @type {(Tramite | Microproceso)}
   * @memberof StorageService
   */
  get flow(): Tramite | Microproceso {
    return this._flow;
  }

  set isSynchronized(v: boolean) {
    this._isSynchronized = v;
    if (this.currentFlowType === 'tramite') {
      this.gis.merge({ tramiteIsSynchronized: v });
    }
  }
  get isSynchronized() {
    return this._isSynchronized;
  }

  set pendingChanges(v: boolean) {
    this._pendingChanges = v;
    this.gis.merge({
      pendingChanges: {
        processAutomation: v
      }
    });
    log.debug('Hay cambios pendientes: ', v);
  }
  get pendingChanges() {
    return this._pendingChanges;
  }

  /**
   * Define el flujo actual con el que el usuario esta trabajando.
   *
   * @memberof StorageService
   */
  setFlow(flowType: FlowType, flow: Tramite | Microproceso, origin: FlowOrigin) {
    // Definir el tipo de flujo con el que se esta trabajando
    this.currentFlowType = flowType;
    // Guardar el flujo en memoria
    this._flow = flow;
    // Definir el estado de sincronización del flujo
    this.isSynchronized = this.getSynchronizationStatus(flowType, flow);
    // Inicializar el flujo en LocalStorage
    this.initLocalFlow(flowType, flow, origin);
    // Refinar actividades del flujo
    this._flow.actividades = this.hfs.refineActivities(this._flow.actividades);
    // Notificar las actividades del flujo
    this.activitiesSub.next(this._flow.actividades.slice());
  }

  /**
   * Define si un flujo esta sincronizado en la DB.
   * Se define que sincronizado para un trámite es cuando este posee
   * minimo una actividad, pero para un microproceso es cuando el id
   * que lo identifica es mayor a 0 o sea positivo.
   *
   * @param {FlowType} flowType
   * @param {(Tramite | Microproceso)} flow
   * @returns {boolean}
   * @memberof StorageService
   */
  getSynchronizationStatus(flowType: FlowType, flow?: Tramite | Microproceso): boolean {
    let isSynchronized = false;
    if (!flow && flowType === 'tramite') {
      flow = <Tramite>this.getFromLocal([this.hfs.flowsHistory[0]], 1);
    }
    if (flow) {
      switch (flowType) {
        case 'tramiteInGoBack':
        case 'tramite':
          let sync = false;
          if (isEmpty(flow.actividades)) {
            if (this.pendingChanges) {
              sync = true;
            } else {
              sync = false;
            }
          } else {
            for (let index = 0; index < flow.actividades.length; index++) {
              if (flow.actividades[index].id < 0) {
                sync = false;
                if (flow.actividades[index].tareas.length) {
                  for (let indexT = 0; indexT < flow.actividades[index].tareas.length; indexT++) {
                    if (flow.actividades[index].tareas[indexT].id < 0) {
                      sync = false;
                    } else {
                      sync = true;
                      break;
                    }
                  }
                }
              } else {
                sync = true;
              }
              if (sync) {
                break;
              }
            }
          }
          isSynchronized = sync;
          break;
        case 'microproces':
          isSynchronized = flow.id < 0 ? false : true;
          break;
        default:
          isSynchronized = false;
          break;
      }
    }
    return isSynchronized;
  }


  /**
   * Define en que array de historial se va realizar una operación.
   *
   * @private
   * @param {GroupActionHistoryType} element
   * @param {ActionHistoryType} type
   * @returns
   * @memberof StorageService
   */
  private switchHistory(element: GroupActionHistoryType, type: ActionHistoryType) {
    let history;
    switch (type) {
      case 'create':
        history = this.creationsHistory[element];
        break;
      case 'update':
        history = this.updatesHistory[element];
        break;
      case 'delete':
        history = this.deletionsHistory[element];
        break;
    }
    return history;
  }

  /**
   * Consulta si una activida tiene historial de cambios.
   *
   * @param {GroupActionHistoryType} element
   * @param {ActionHistoryType} type
   * @param {number} id
   * @returns {ActionHistory}
   * @memberof StorageService
   */
  getActionHistory(element: GroupActionHistoryType, type: ActionHistoryType, id: number): ActionHistory {
    let action: ActionHistory = null;
    const nameId = element === 'activities' ? 'activityId' : 'taskId';
    action = find(this.switchHistory(element, type), [nameId, id]);
    return action;
  }

  /**
   * Agrega una actividad al historial de cambios.
   *
   * @param {GroupActionHistoryType} element
   * @param {ActionHistoryType} type
   * @param {ActionHistory} action
   * @memberof StorageService
   */
  addActionHistory(element: GroupActionHistoryType, type: ActionHistoryType, action: ActionHistory) {
    const history = this.switchHistory(element, type);
    // Consultar si existe la acción dentro del historial seleccionado
    let index = findIndex(history, action);
    index = index === -1 ? history.length : index;
    // Si existe la acción dentro del historial; se replanza de lo contrario lo agregrea
    history.splice(index, 1, action);
    log.debug(`Nuevo '${type}' registrado en el historial de '${element}.'`, history);
  }

  /**
   * Remueve una actividad del historial de cambios.
   *
   * @param {GroupActionHistoryType} element
   * @param {ActionHistoryType} type
   * @param {number} id
   * @memberof StorageService
   */
  removeActionHistory(element: GroupActionHistoryType, type: ActionHistoryType, id: number) {
    remove(this.switchHistory(element, type), (el) => {
      if (element === 'activities') {
        return el.activityId === id;
      } else {
        return el.taskId === id;
      }
    });
  }

  /**
   * Si el flujo esta sincronizado con la DB entonces se registra
   * una acción (create, update y delete) en el historial(activities, tasks).
   *
   * @param {GroupActionHistoryType} element
   * @param {ActionHistoryType} type
   * @param {(ActionHistory | number)} data
   * @memberof StorageService
   */
  registerActionInHistory(
    element: GroupActionHistoryType,
    type: ActionHistoryType,
    data: ActionHistory | number,
    idFind?: number) {
    if (this.getSynchronizationStatus('tramite')) {
      let taskIsFromDB = false;
      switch (type) {
        case 'create':
          this.addActionHistory(element, 'create', data as ActionHistory);
          log.debug(`Create en ${element}: registrado correctamente.`);
          break;
        case 'update':
          if (idFind) {
            taskIsFromDB = this.getActionHistory(
              element, 'create', idFind
            ) ? false : true;
            if (taskIsFromDB) {
              this.addActionHistory(element, 'update', data as ActionHistory);
              log.debug(`Update en ${element}: registrado correctamente.`);
            }
          }
          break;
        case 'delete':
          if (idFind) {
            taskIsFromDB = this.getActionHistory(
              element, 'create', idFind
            ) ? false : true;
            if (taskIsFromDB) {
              this.addActionHistory(element, 'delete', data as ActionHistory);
              const taskModificate = this.getActionHistory(
                element, 'update', idFind
              ) ? false : true;
              if (!taskModificate) {
                this.removeActionHistory(element, 'update', idFind);
              }
              log.debug(`Delete en ${element}: registrado correctamente.`);
            } else {
              this.removeActionHistory(element, 'create', idFind);
              log.debug(`Create en ${element}: eliminado correctamente.`);
            }
          }
          break;
      }
    }
  }

  /**
   * Iniciar un flujo que no tiene actividades y por tanto
   * no esta sincronizado con la DB.
   *
   * @private
   * @param {FlowType} flowType
   * @param {(Tramite | Microproceso)} flow
   * @memberof StorageService
   */
  private initLocalFlow(flowType: FlowType, flow: Tramite | Microproceso, origin: FlowOrigin) {
    // Consultar flujo en LocalStorage
    let localFlow = null;
    // Si el flujo es un tramite de primer nivel
    if (flowType === 'tramite' || flowType === 'tramiteInGoBack') {
      localFlow = this.getFromLocal([flow.id], 1) as Tramite;
    } else if (flowType === 'microproces') {
      localFlow = <Microproceso>this.getMicroProcess([(<Microproceso>flow).id], true);
    } else {
      log.error(`Error en initLocalFlow: el tipo de flujo no permitido.`);
    }
    // Si no existe la referencia del flujo en localstorage, se crea
    if (!localFlow) {
      // Crear nuevo flujo local en base a la
      // información de consultada de la DB.
      if (flowType === 'tramite') {
        this.pendingChanges = this.isSynchronized ? false : flow.actividades.length ? true : false;
        this.localFlows.push({
          tramite: flow as Tramite,
          microproces: [],
          meta: {
            created: new Date(),
            pendingChanges: this.pendingChanges,
            origin: origin,
            user: this.authUser.user_id
          }
        });
      } else if (flowType === 'microproces') {
        const idTramite = this.hfs.flowsHistory[0];
        const indexTramite = this.getFromLocal([idTramite], 1, true) as number;
        if (isNumber(indexTramite) && indexTramite !== -1) {
          this.localFlows[indexTramite].microproces.push(flow as Microproceso);
        } else {
          log.error(`
            No se encuentra el trámite ${this.hfs.flowsHistory[0]} en LocalStorage para crear el nuevo micro proceso.
          `);
        }
      }
      // Notificar dev
      log.debug(`Flujo local ${flow.id} inicializado correctamente.`);
    } else {
      if (!this.isSynchronized) {
        // Actualizar flujo consultado de la DB con el iniciado en LocalStorage
        this._flow = localFlow;
        // Actualizar los consecutivos con el id menor,
        // si dicho flujo posee actividades.
        if (!isEmpty(localFlow.actividades)) {
          this.updateAllConsecutive(origin);
        }
      } else {
        // Se le da más importancia al flujo de la DB.
        if (flowType === 'tramite' || flowType === 'tramiteInGoBack') {
          // Consultar indice del flujo en local, para actualizarlo.
          const flowID = !this.hfs.flowsHistory.length ? flow.id : this.hfs.flowsHistory[0];
          const indexLocalFlow = this.getFromLocal([flowID], 0, true) as number;
          // Actualizar trámite local con el flujo de DB.
          this.localFlows[indexLocalFlow].tramite = flow as Tramite;
          // Verificar si el flujo no tiene nada pendiente que impida borrar la información actual.
          if (flowType === 'tramite' && !this.flowIsBlocked(flowID)) {
            // Limpiar los micro procesos consultados.
            this.localFlows[indexLocalFlow].microproces = [];
          }
        }
      }
      // Notificar dev
      log.debug(`El flujo local ${flow.id} ya esta iniciado en LocalStorage.`);
    }
    // Actualizar el flujos en LocalStorage
    this.saveInLocal();
  }

  /**
   * Define si un determinado flujo local esta bloqueado
   * a la hora de eliminar información del mismo.
   * Los criterios de bloqueo son los siguientes:
   * - Que el flujo tenga cambios pendientes sin sincronizar.
   *
   * @param {number} flowId
   * @memberof StorageService
   */
  flowIsBlocked(flowId: number): boolean {
    const localFlow = this.getFromLocal([flowId], 0) as LocalFlow;
    if (localFlow && localFlow.hasOwnProperty('meta')) {
      return localFlow.meta.pendingChanges;
    }
    return false;
  }

  /**
   * Reinicia el contador de un consecutivo en especifico.
   *
   * @param {string} key
   * @memberof StorageService
   */
  resetId(key: IdType) {
    this.id[key] = 0;
  }

  /**
   * Obtener el último id asignado a una actividad o tarea.
   *
   * @param {IdType} key
   * @param {FlowOrigin} origin
   * @returns {number}
   * @memberof StorageService
   */
  getLastId(key: IdType, origin: FlowOrigin): number {
    let lastId = 0;
    switch (key) {
      case 'activity':
        const activities: Actividad[] = [];

        if (this.localFlows) {
          this.localFlows.forEach(flw => {
            if (flw.tramite.id === origin.tramite) {
              if (flw.tramite) {
                flw.tramite.actividades.forEach(actF => activities.push(actF));
              }
              if (flw.microproces) {
                flw.microproces.forEach(microFl => {
                  if (microFl.actividades) {
                    microFl.actividades.forEach(actM => activities.push(actM));
                  }
                });
              }
            }
          });
        }
        if (activities && activities.length) {
          lastId = orderBy(activities, ['id'])[0]['id'];
        }
        break;
      case 'task':
        const tasks: Tarea[] = [];

        if (this.localFlows) {
          this.localFlows.forEach(flw => {
            if (flw.tramite.id === origin.tramite) {
              if (flw.tramite) {
                flw.tramite.actividades.forEach(actF => {
                  if (actF.tareas) {
                    actF.tareas.forEach(tskT => tasks.push(tskT));
                  }
                });
              }
              if (flw.microproces) {
                flw.microproces.forEach(microFl => {
                  if (microFl.actividades) {
                    microFl.actividades.forEach(actM => {
                      if (actM.tareas) {
                        actM.tareas.forEach(tskM => tasks.push(tskM));
                      }
                    });
                  }
                });
              }
            }
          });
        }
        if (tasks && tasks.length) {
          lastId = orderBy(tasks, ['id'])[0]['id'];
        }
        break;
      case 'option':
        const options: TareaOpcionAdmin[] = [];

        if (this.localFlows) {
          this.localFlows.forEach(flw => {
            if (flw.tramite.id === origin.tramite) {
              if (flw.tramite) {
                flw.tramite.actividades.forEach(activity => {
                  if (activity.tareas) {
                    activity.tareas.forEach(task => {
                      if (task.id < 0 && !isEmpty(task.tarea_condicion)) {
                        (<TareaCondicionAdmin[]>task.tarea_condicion).forEach(conditions => {
                          conditions.opciones.forEach(op => options.push(op));
                        });
                      }
                    });
                  }
                });
              }
              if (flw.microproces && flw.microproces.length) {
                flw.microproces.forEach(microFl => {
                  if (microFl.actividades && microFl.actividades.length) {
                    microFl.actividades.forEach(actM => {
                      if (actM.tareas && actM.tareas.length) {
                        actM.tareas.forEach(taskM => {
                          if (taskM.tarea_condicion && taskM.tarea_condicion.length) {
                            (<TareaCondicionAdmin[]>taskM.tarea_condicion).forEach(conditions => {
                              if (conditions.opciones && conditions.opciones.length) {
                                conditions.opciones.forEach(op => options.push(op));
                              }
                            });
                          }
                        });
                      }
                    });
                  }
                });
              }
            }
          });
        }

        const lastIds: number[] = [];
        if (options.length) {
          lastIds.push(minBy(options, 'id_opcion')['id_opcion']);
          lastId = min(lastIds);
        }

        if (lastId > 0) {
          lastId = 0;
        }
        break;
    }
    return lastId;
  }

  /**
   * Actualizar todos los consecutivos almacenados en 'this.id'
   * con el los IDs menores de cada categoría.
   *
   * @param {FlowOrigin} origin
   * @memberof StorageService
   */
  updateAllConsecutive(origin: FlowOrigin) {
    // Actualizar los consecutivos con el id menor
    this.id.activity = this.getLastId('activity', origin);
    this.id.task = this.getLastId('task', origin);
    this.id.option = this.getLastId('option', origin);

    if (this.id.activity > 0) {
      this.id.activity = 0;
    }
    if (this.id.task > 0) {
      this.id.task = 0;
    }
    if (this.id.option > 0) {
      this.id.option = 0;
    }

  }

  /**
   * Consulta el siguiente id para crear un recurso.
   *
   * @param {(IdType)} resource
   * @returns
   * @memberof StorageService
   */
  getNextId(resource: IdType, from: number = 1): number {
    return this.id[resource] - from;
  }

  /**
   * Aumenta el contador del id de un recurso.
   *
   * @param {(IdType)} resource
   * @memberof StorageService
   */
  applyNextId(resource: IdType, from: number = 1): number {
    this.id[resource] = this.id[resource] - from;
    return this.id[resource];
  }

  /**
   * Setea la las actividades de un flujo local.
   *
   * @memberof StorageService
   */
  setActivities(value: Actividad[]) {
    // Consultar el trámite en los flujos locales (por seguridad)
    const flow = this._flow.id === this.hfs.flowsHistory[0] ?
      this.getFromLocal([this._flow.id], 1) as Tramite :
      <Microproceso>this.getMicroProcess([this.flow.id], true);
    setTimeout(() => {
      if (flow) {
        // Actualizar actividades del flujo alamacenado en memoria
        flow.actividades = value;
        // Notificar las actividades del flujo
        this.activitiesSub.next(this._flow.actividades.slice());
        if (this.isSynchronized && !this.historiesAreEmpty()) {
          this.pendingChanges = true;
        }
        // Actualizar el flujos en LocalStorage
        this.saveInLocal();
        // Informar en dev
        log.info(`Se han actualizado las actividades del flujo local ${this._flow.id}`);
      } else {
        log.error(`No se encuentra la referencia del flujo guardado en memoria de LocalStorage.`);
      }
    });
  }

  /**
   * Actualiza las keys de un microproceso en base a su tarea de origen.
   *
   * @param {Tarea} task
   * @memberof StorageService
   */
  updateMicroProcess(task: Tarea) {
    // Consultar el indice del flujo local actual
    const localFlowIndex = Number(this.getFromLocal([this.hfs.flowsHistory[0]], 0, true));
    // Consultar el indice del microproceso que coincide con la tarea
    const microIndex = Number(this.getMicroProcess([task.id], true, true));
    // Keys que se deben actualizar en el microproceso con respecto a al tarea
    const keysForUpdate = ['nombre', 'orden'];

    // Se encontro satisfactoriamente el microproceso
    if (localFlowIndex >= 0 && microIndex >= 0) {
      const micro = this.localFlows[localFlowIndex].microproces[microIndex];
      keysForUpdate.forEach(key => {
        micro[key] = task[key];
      });
    }
  }

  /**
   * Retorna una copia de las actividades del flujo.
   * Nota: Se usan capias para evitar manipular la referencia original.
   *
   * @returns {Observable<Actividad[]>}
   * @memberof StorageService
   */
  getActivities(): Observable<Actividad[]> {
    // Crear copia de las actividades del flujo
    return this.activitiesSub.asObservable();
  }

  /**
   * Filtra un objeto dentro de los flujos almacenados en LocalStorage.
   * Se puede acceder al flujo, trámite, actividad y tarea especificando
   * el nivel(param -> level) de profundidad de la busqueda.
   *
   * Las devoluciones puden ser el objeto filtrado o el indice que
   * ocupa dentro del array, si se usa el parametro 'returnIndex'
   * en true, retorna el número de indice.
   *
   * El orden de los IDs es: idTramite, idActivity y idTask.
   * Los niveles(parametro: 'level') son:
   *  - 0 ==> Flujo local
   *  - 1 ==> Trámite
   *  - 2 ==> Actividad
   *  - 3 ==> Tarea
   *
   * @param {number[]} ids - IDs en orden de profundidad para encontrar el objeto a filtrar.
   * @param {number} level - Nivel de filtro deseado.
   * @returns
   * @memberof StorageService
   */
  getFromLocal(
    ids: number[],
    level: number,
    returnIndex: boolean = false
  ): LocalFlow | Tramite | Actividad | Tarea | Number | null {
    const [idTramite, idActivity, idTask] = ids;
    let flowLocal = null,
      activity = null,
      task = null;

    if (!returnIndex) {
      flowLocal = this.localFlows.find(item => {
        return item.tramite.id === idTramite;
      });
      if (idActivity && flowLocal) {
        activity = find(flowLocal.tramite.actividades, { id: idActivity });
      }
      if (idTask && activity) {
        task = find(activity.tareas, { id: idTask });
      }
    } else {
      flowLocal = this.localFlows.findIndex(item => {
        return item.tramite.id === idTramite;
      });
      if (idActivity && flowLocal !== -1) {
        activity = findIndex(this.localFlows[flowLocal].tramite.actividades, { id: idActivity });
      }
      if (idTask && activity !== -1) {
        task = findIndex(this.localFlows[flowLocal].tramite.actividades[activity].tareas, { id: idTask });
      }
    }
    switch (level) {
      case 0:
        return flowLocal;
      case 1:
        if (isNumber(flowLocal)) {
          return flowLocal;
        } else {
          if (flowLocal) {
            return flowLocal.tramite;
          }
        }
        return null;
      case 2:
        return activity;
      case 3:
        return task;
      default:
        return null;
    }
  }

  /**
   * Cunsulta un microproceso de un tramite en especifico.
   *
   * @param {number[]} ids - IDs del tramite y el microproceso.
   * @returns {Microproceso}
   * @memberof StorageService
   */
  getMicroProcess(ids: number[], currentTramite: boolean = false, returnIndex: boolean = false): Microproceso | number {
    const tramiteId = currentTramite ? this.hfs.flowsHistory[0] : ids[0];
    const microId = currentTramite ? ids[0] : ids[1];
    const localFlow = <LocalFlow>this.getFromLocal([tramiteId], 0);
    if (localFlow) {
      const fn = returnIndex ? findIndex : find;
      return fn(localFlow.microproces, { id: microId });
    }
    return null;
  }

  /**
   * Elimina un flujo local.
   *
   * @param {number} idTramite
   * @memberof StorageService
   */
  removeLocalFlow(idTramite: number): boolean {
    const localFlow = <LocalFlow>this.getFromLocal([idTramite], 0);
    if (localFlow) {
      const res = remove(this.localFlows, (el) => {
        return el.tramite.id === localFlow.tramite.id;
      });
      if (res) {
        this._flow = null;
        this.activitiesSub.next([]);
        this.saveInLocal();
        log.debug(`Se elimino el flujo local ${localFlow.tramite.id} correctamente`);
        return true;
      } else {
        log.debug(`No se pudó eliminar el flujo local ${localFlow.tramite.id}.`);
      }
      return false;
    }
  }

  /**
   * Sincroniza el flujo local actual en la base de datos.
   *
   * @returns {Promise<boolean>}
   * @memberof StorageService
   */
  async syncInDB(): Promise<boolean> {
    let localFlow = <LocalFlow>this.getFromLocal([this.hfs.flowsHistory[0]], 0);
    let res;
    localFlow = this.cleanLocalFlow(localFlow);
    if (localFlow) {
      // Definir el estado de sincronización del TRÁMITE
      const tramiteIsSynchronized = await this.getSynchronizationStatus('tramite');
      if (!tramiteIsSynchronized) {
        const flowFormatted = new TramiteDefaultAdmin({
          tramite: localFlow.tramite,
          microproces: localFlow.microproces
        });
        if (this.typeProcess === 'process') {
          res = await this.pas.newDefaultWorkflowTramite(flowFormatted);
        } else {
          res = await this.pas.newDefaultWorkflowSecondaryProcess(flowFormatted);
        }
      } else {
        res = await this.updateInDB(localFlow);
      }
      // Si se sincroniza el flujo en DB se elimina la referencia de LocalStorage.
      if (res) {
        this.removeLocalFlow(this.hfs.flowsHistory[0]);
        this.pendingChanges = false;
      }
      return res;
    }
  }

  /**
   * Actualiza un flujo local en la base de datos.
   *
   * @param {LocalFlow} flow
   * @returns
   * @memberof StorageService
   */
  async updateInDB(flow: LocalFlow) {
    const groupActions: any = {
      crear: {
        actividades: [],
        tareas: []
      },
      modificar: {
        actividades: [],
        tareas: []
      },
      eliminar: {
        actividades: [],
        tareas: []
      }
    };

    const getAllTasks = (activities: Actividad[]) => {
      let tasks: Tarea[] = [];
      activities.map(a => {
        tasks = [...tasks, ...a.tareas];
      });
      return tasks;
    };

    const setGroup = (
      items: Actividad[] | Tarea[],
      group: any[],
      history: ActionHistory[],
      keyId: 'activityId' | 'taskId'
    ) => {
      history.forEach(el => {
        const search = find(items, { id: el[keyId] });
        if (search) {
          group.push(search);
        } else {
          // agrega al array las key para cuando es eliminar
          if (keyId === 'activityId') {
            if (el.activityId > 0) {
              group.push({ id: el.activityId });
            }
          } else {
            if (el.taskId > 0) {
              group.push({ id: el.taskId });
            }
          }
        }
      });
    };

    // Reunir en un solo lugar todas las actividades del tramite
    let allActivities: Actividad[] = flow.tramite.actividades.slice();
    flow.microproces.slice().map(m => {
      allActivities = [
        ...allActivities,
        ...m.actividades,
      ];
    });
    // Reunir en un solo lugar todas las tareas del tramite
    const allTasks: Tarea[] = getAllTasks(allActivities);
    // Eliminar key 'tareas' de todas las actividades
    allActivities.map(a => {
      delete a.tareas;
    });
    const actions = {
      create: 'crear',
      update: 'modificar',
      delete: 'eliminar'
    };
    // define cuando es valido sincronizar el flujo con la base de datos
    let valid = false;
    // Agrupar actividades por acción
    Object.keys(actions).forEach(key => {
      const group = groupActions[actions[key]];
      const activityHistory = this.switchHistory(
        'activities', key as ActionHistoryType
      );
      const taskHistory = this.switchHistory(
        'tasks', key as ActionHistoryType
      );
      setGroup(allActivities, group.actividades, activityHistory, 'activityId');
      setGroup(allTasks, group.tareas, taskHistory, 'taskId');
      if (group.actividades.length || group.tareas.length) {
        valid = true;
      }
    });
    if (valid) {
      let res: boolean;
      if (this.typeProcess === 'process') {
        res = await this.pas.updateDefaultWorkflowTramite(
          this.hfs.flowsHistory[0], groupActions
        );
      } else {
          res = await this.pas.updateDefaultWorkflowSecondaryProcess(
            this.hfs.flowsHistory[0], groupActions
          );
      }
      // Si se sincroniza el flujo en DB se elimina la referencia de LocalStorage.
      if (res) {
        this.removeLocalFlow(this.hfs.flowsHistory[0]);
        this.clearHistory();
      }
      return res;
    } else {
      return Promise.resolve(true);
    }
  }

  /**
   * Limpia las variables encargadas de guardar el historial de las acciones en el modificar
   *
   * @private
   * @memberof StorageService
   */
  clearHistory() {
    // Borrar en memoria
    const histories: string[] = ['creationsHistory', 'updatesHistory', 'deletionsHistory'];
    histories.forEach(key => {
      this[key].activities = [];
      this[key].tasks = [];
    });
  }

  /**
   * Sobreescribe la información de los flujos en LocalStorage
   * con los flujos en memoria(this.localFlows).
   *
   * @private
   * @memberof StorageService
   */
  private saveInLocal() {
    // Actualizar el flujos en LocalStorage
    const cleanLocalFlows = clone(this.localFlows).map((f) => {
      return this.cleanLocalFlow(f);
    });
    if (cleanLocalFlows.length) {
      log.debug('saveInLocal ==> ', cleanLocalFlows);
    }
    // Guarda los flujos locales
    if (this.typeProcess === 'process') {
      this.lss.store(Const.KEY_LS_DEFAULT_FLOWS.process, cleanLocalFlows);
    } else {
      this.lss.store(Const.KEY_LS_DEFAULT_FLOWS.secondaryProcess, cleanLocalFlows);
    }
  }

  /**
   * Valida si los historiales de creación, modificación y eliminación estan vacios.
   *
   * @returns {boolean}
   * @memberof StorageService
   */
  historiesAreEmpty(): boolean {
    const histories: GroupActionHistory[] = [this.creationsHistory, this.updatesHistory, this.deletionsHistory];
    const validations = histories.map(h => {
      return isEmpty(h.activities) && isEmpty(h.tasks);
    });
    return validations.includes(false) ? false : true;
  }

  /**
   * Limpia keys innecesarias del flujo que produce errores al guardar en LocalStorage.
   *
   * @param {LocalFlow} flow
   * @param {string[]} [keys=[]]
   * @returns
   * @memberof StorageService
   */
  cleanLocalFlow(flow: LocalFlow, keys: string[] = []) {
    const newflow = clone(flow);
    const keysForClean = [...[
      'meta',
      'asignationRoles',
      'responsibleRoles',
      'selectRolesTpl',
      'statusOptions',
      'typesOptions'
    ], ...keys];

    const clean = (items: any[]) => {
      if (items && items.length) {
        items.forEach((item, i) => {
          const _omit = omit(item, keysForClean);
          items[i] = _omit;
        });
      }
    };
    // Limpiar actividades del tramite principal
    clean(newflow.tramite.actividades);
    // Limpiar tareas del tramite principal
    newflow.tramite.actividades.map(a => {
      clean(a.tareas);
    });
    // Limpiar cada microproceso
    newflow.microproces.map(m => {
      // Limpiar actividades
      clean(m.actividades);
      // Limpiar tareas
      m.actividades.map(a => {
        clean(a.tareas);
      });
    });
    return newflow;
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/shared/storage.service.ts